import { MoneyPresentationProvider } from "@/lib/money-format";
import { Outlet, createRootRoute } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { AuthProvider } from "@/context/AuthContext";
import { TeamProvider } from "@/lib/team-context";
import { CompanyProvider } from "@/lib/company-context";
import { Toaster } from "@/components/ui/sonner";
import { queryClient } from "@/lib/queryClient";
import { CashierBoundary } from "@/components/layout/CashierBoundary";
import { SandboxBanner } from "@/components/layout/SandboxBanner";

/**
 * Root route — wires every cross-cutting provider:
 *
 *   ThemeProvider (next-themes, light/dark/system)
 *     └─ QueryClientProvider (react-query — single global cache)
 *         └─ AuthProvider (OpenAuth verify + signIn/signOut)
 *             └─ TeamProvider (active team id, persisted, X-Team-Id header)
 *                 └─ CompanyProvider (active company id, persisted,
 *                                     X-Company-Id header for /api/me/dian)
 *                     └─ <SandboxBanner /> (HAB-only chrome on sandbox hosts)
 *                     └─ <CashierBoundary><Outlet /></CashierBoundary> (routed page)
 *                     └─ <Toaster /> (sonner, themed via next-themes)
 *
 * No chrome lives at the root — pages decide their own layout. The
 * authenticated portal pages compose `<AppShell>` themselves so the
 * `/login` and `/auth/callback` pages can render without it.
 */
export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <MoneyPresentationProvider>
          <TeamProvider>
            <CompanyProvider>
              <SandboxBanner />
              <CashierBoundary><Outlet /></CashierBoundary>
              <Toaster />
            </CompanyProvider>
          </TeamProvider>
          </MoneyPresentationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
